import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { TranscriptionStatus } from '@klaro7/shared-types';
import { Card, CardContent, Button, Spinner, Avatar } from '@klaro7/ui';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useCallHistory, useTranscription } from '@/features/calls/hooks/useCallHistory';
import { callStatusInfo } from '@/features/calls/lib/callStatusInfo';
import { resolveUploadUrl } from '@/lib/uploads';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function renderStars(rating: number) {
  return (
    <div className="flex gap-0.5" role="img" aria-label={`Valutazione: ${rating} stelle su 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`h-4 w-4 ${star <= rating ? 'text-brand-orange fill-brand-orange' : 'text-neutral-300'}`}
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
          />
        </svg>
      ))}
    </div>
  );
}

const transcriptionStatusLabels: Record<TranscriptionStatus, string> = {
  none: 'Nessuna registrazione',
  processing: 'Trascrizione in corso...',
  completed: '',
  failed: 'Trascrizione fallita',
  skipped: 'Chiamata troppo breve per la trascrizione',
};

// ---- Transcription Modal ----

function TranscriptionModal({
  sessionId,
  sessionName,
  onClose,
}: {
  sessionId: string;
  sessionName: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useTranscription(sessionId);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="mx-4 max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Trascrizione</h3>
            <p className="text-sm text-neutral-500">{sessionName}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-4" style={{ maxHeight: 'calc(80vh - 80px)' }}>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : !data || data.transcriptionStatus === 'none' ? (
            <div className="py-8 text-center">
              <svg className="mx-auto mb-3 h-12 w-12 text-neutral-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
              </svg>
              <p className="text-neutral-500">{transcriptionStatusLabels.none}</p>
            </div>
          ) : data.transcriptionStatus === 'processing' ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Spinner size="lg" />
              <p className="text-neutral-500">{transcriptionStatusLabels.processing}</p>
            </div>
          ) : data.transcriptionStatus === 'failed' ? (
            <div className="py-8 text-center">
              <svg className="mx-auto mb-3 h-12 w-12 text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <p className="text-red-600">{transcriptionStatusLabels.failed}</p>
            </div>
          ) : data.transcriptionStatus === 'skipped' ? (
            <div className="py-8 text-center">
              <svg className="mx-auto mb-3 h-12 w-12 text-neutral-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
              </svg>
              <p className="text-neutral-500">{transcriptionStatusLabels.skipped}</p>
            </div>
          ) : (
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-700">
              {data.transcription}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Search Input ----

function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative">
      <svg
        className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-neutral-200 py-2 pl-9 pr-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30"
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-neutral-400 hover:text-neutral-600"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ---- Professional view: call history ----

function ProfessionalConsultations() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedSessionName, setSelectedSessionName] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useCallHistory({
    page,
    limit: 20,
    search: debouncedSearch || undefined,
  });

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <div className="mb-4">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Cerca per nome cliente..."
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : !data?.data?.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="mb-4 rounded-full bg-neutral-100 p-4">
                <svg className="h-8 w-8 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                </svg>
              </div>
              <p className="font-medium text-neutral-600">
                {debouncedSearch ? 'Nessun risultato' : 'Nessuna consulenza'}
              </p>
              <p className="mt-1 text-sm text-neutral-400">
                {debouncedSearch
                  ? `Nessuna consulenza trovata per "${debouncedSearch}"`
                  : 'Le chiamate e videochiamate con i tuoi clienti appariranno qui'}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {data.data.map((session) => {
                  const statusInfo = callStatusInfo(session);
                  const caller = session.callerId as unknown as {
                    id?: string;
                    profile?: { firstName?: string; lastName?: string; avatar?: string };
                    email?: string;
                  };
                  const callerName = caller?.profile
                    ? `${caller.profile.firstName || ''} ${caller.profile.lastName || ''}`.trim()
                    : caller?.email || 'Utente';

                  const hasTranscription = session.transcriptionStatus === 'completed' || session.transcriptionStatus === 'processing';

                  return (
                    <div
                      key={session.id}
                      className={`flex flex-col gap-3 rounded-lg border border-neutral-200 p-4 transition-colors sm:flex-row sm:items-center sm:justify-between ${
                        session.status === 'completed' ? 'cursor-pointer hover:bg-neutral-50' : ''
                      }`}
                      onClick={() => {
                        if (session.status === 'completed') {
                          setSelectedSessionId(session.id);
                          setSelectedSessionName(callerName);
                        }
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <Avatar
                            size="sm"
                            src={resolveUploadUrl(caller?.profile?.avatar)}
                            fallback={callerName}
                            alt={callerName}
                          />
                          <div className={`absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white ${
                            session.callType === 'video' ? 'bg-brand-teal' : 'bg-blue-500'
                          }`}>
                            {session.callType === 'video' ? (
                              <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                              </svg>
                            ) : (
                              <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <div>
                          <p className="font-medium text-neutral-900">{callerName}</p>
                          <p className="text-xs text-neutral-400">
                            {session.callType === 'video' ? 'Videochiamata' : 'Chiamata audio'}{' \u00B7 '}
                            {new Date(session.initiatedAt).toLocaleDateString('it-IT', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                        {session.durationSeconds > 0 && (
                          <div className="text-sm">
                            <span className="text-neutral-400">Durata: </span>
                            <span className="font-medium text-neutral-700">{formatDuration(session.durationSeconds)}</span>
                          </div>
                        )}
                        {session.proEarning != null && session.proEarning > 0 && (
                          <div className="text-sm">
                            <span className="text-neutral-400">Guadagno: </span>
                            <span className="font-medium text-neutral-700">{'\u20AC'}{session.proEarning.toFixed(2)}</span>
                          </div>
                        )}
                        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                        {session.callerRating?.overall != null && session.callerRating.overall > 0 && renderStars(session.callerRating.overall)}
                        {hasTranscription && (
                          <span className="inline-flex items-center gap-1 text-xs text-brand-teal">
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                            </svg>
                            Trascrizione
                          </span>
                        )}
                        {session.status === 'completed' && caller?.id && (
                          <Link
                            to={`/my/chat?user=${caller.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 px-2.5 py-1 text-xs font-medium text-neutral-600 hover:border-brand-teal hover:text-brand-teal"
                          >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                            </svg>
                            Scrivi
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {data.pagination && data.pagination.totalPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!data.pagination.hasPrevPage}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Precedente
                  </Button>
                  <span className="text-sm text-neutral-500">
                    Pagina {data.pagination.page} di {data.pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!data.pagination.hasNextPage}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Successiva
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {selectedSessionId && (
        <TranscriptionModal
          sessionId={selectedSessionId}
          sessionName={selectedSessionName}
          onClose={() => setSelectedSessionId(null)}
        />
      )}
    </>
  );
}

// ---- User view: consultations list (now using call history) ----

function UserConsultations() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedSessionName, setSelectedSessionName] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useCallHistory({
    page,
    limit: 20,
    search: debouncedSearch || undefined,
  });

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <div className="mb-4">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Cerca per nome consulente..."
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : !data?.data?.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="mb-4 rounded-full bg-neutral-100 p-4">
                <svg className="h-8 w-8 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="font-medium text-neutral-600">
                {debouncedSearch ? 'Nessun risultato' : 'Nessuna consulenza'}
              </p>
              <p className="mt-1 text-sm text-neutral-400">
                {debouncedSearch
                  ? `Nessuna consulenza trovata per "${debouncedSearch}"`
                  : 'Le tue consulenze appariranno qui'}
              </p>
              {!debouncedSearch && (
                <Button variant="primary" className="mt-4 bg-brand-orange hover:bg-brand-orange/90" asChild>
                  <Link to="/">Trova un Professionista</Link>
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {data.data.map((session) => {
                  const statusInfo = callStatusInfo(session);
                  const professional = session.professionalId as unknown as {
                    title?: string;
                    userId?: { profile?: { firstName?: string; lastName?: string; avatar?: string } };
                  };
                  const professionalName = professional?.userId?.profile
                    ? `${professional.userId.profile.firstName || ''} ${professional.userId.profile.lastName || ''}`.trim()
                    : 'Professionista';
                  const professionalAvatar = professional?.userId?.profile?.avatar;

                  const hasTranscription = session.transcriptionStatus === 'completed' || session.transcriptionStatus === 'processing';

                  return (
                    <div
                      key={session.id}
                      className={`flex flex-col gap-3 rounded-lg border border-neutral-200 p-4 transition-colors sm:flex-row sm:items-center sm:justify-between ${
                        session.status === 'completed' ? 'cursor-pointer hover:bg-neutral-50' : ''
                      }`}
                      onClick={() => {
                        if (session.status === 'completed') {
                          setSelectedSessionId(session.id);
                          setSelectedSessionName(professionalName);
                        }
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <Avatar
                            size="sm"
                            src={resolveUploadUrl(professionalAvatar)}
                            fallback={professionalName}
                            alt={professionalName}
                          />
                          <div className={`absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white ${
                            session.callType === 'video' ? 'bg-brand-teal' : 'bg-blue-500'
                          }`}>
                            {session.callType === 'video' ? (
                              <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                              </svg>
                            ) : (
                              <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <div>
                          <p className="font-medium text-neutral-900">{professionalName}</p>
                          <p className="text-xs text-neutral-400">
                            {session.callType === 'video' ? 'Videochiamata' : 'Chiamata audio'}{' \u00B7 '}
                            {new Date(session.initiatedAt).toLocaleDateString('it-IT', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                        {session.durationSeconds > 0 && (
                          <div className="text-sm">
                            <span className="text-neutral-400">Durata: </span>
                            <span className="font-medium text-neutral-700">{formatDuration(session.durationSeconds)}</span>
                          </div>
                        )}
                        {session.totalCost > 0 && (
                          <div className="text-sm">
                            <span className="text-neutral-400">Costo: </span>
                            <span className="font-medium text-neutral-700">{'\u20AC'}{session.totalCost.toFixed(2)}</span>
                          </div>
                        )}
                        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                        {session.callerRating?.overall != null && session.callerRating.overall > 0 && renderStars(session.callerRating.overall)}
                        {hasTranscription && (
                          <span className="inline-flex items-center gap-1 text-xs text-brand-teal">
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                            </svg>
                            Trascrizione
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {data.pagination && data.pagination.totalPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!data.pagination.hasPrevPage}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Precedente
                  </Button>
                  <span className="text-sm text-neutral-500">
                    Pagina {data.pagination.page} di {data.pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!data.pagination.hasNextPage}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Successiva
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {selectedSessionId && (
        <TranscriptionModal
          sessionId={selectedSessionId}
          sessionName={selectedSessionName}
          onClose={() => setSelectedSessionId(null)}
        />
      )}
    </>
  );
}

// ---- Main page ----

export function ConsultationsPage() {
  const { user } = useAuth();
  const isProfessional = user?.role === 'professional';

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Le Mie Consulenze</h1>
          <p className="text-neutral-500">
            {isProfessional
              ? 'Storico delle chiamate e videochiamate con i tuoi clienti'
              : 'Storico delle tue consulenze con i professionisti'}
          </p>
        </div>

        {isProfessional ? <ProfessionalConsultations /> : <UserConsultations />}
      </div>
    </div>
  );
}
